import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { UNAUTHENTICATED, api } from "../api/client";
import { forgetAddress, rememberAddress } from "./remembered";
import { forgetScope } from "./scope";
import { leave, signOutAddress } from "./signout";
import type { components } from "../api/schema";

export type Me = components["schemas"]["Me"];

interface Session {
  me: Me | undefined;
  /** `leaving` is a sign-out whose Zenith half is done and whose browser is
   * on its way to end its session at the provider (ADR-0261). */
  status: "loading" | "anonymous" | "signed-in" | "leaving";
  signIn(email: string, password: string): Promise<components["schemas"]["Problem"] | undefined>;
  /** The provider door (#820): an ID token exchanged once for the session the
   * password mints. True when a session started. It does not refresh — the
   * page moves off the callback address first, then calls refresh, so the
   * shell never draws that address as a page. */
  exchange(idToken: string): Promise<boolean>;
  /** Ends the session. For a session minted from an identity it then sends
   * the browser to end its session at the provider arpIQ runs, and the
   * browser comes back to the signed-out page (ADR-0261); `returnTo` is an
   * invitation that page returns to. Every other session is anonymous here,
   * as before. */
  signOut(opts?: { returnTo?: string }): Promise<void>;
  refresh(): Promise<void>;
}

const Ctx = createContext<Session | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me>();
  const [status, setStatus] = useState<Session["status"]>("loading");

  const refresh = useCallback(async () => {
    const { data } = await api.GET("/me");
    // The address the sign-in page opens on once this session ends by itself
    // (#2054). A break-glass session is never remembered: its door is another
    // page, and an emergency sign-in leaves no trace on the one the next
    // operator meets.
    if (data && !data.break_glass) rememberAddress(data.user.email);
    if (data) { setMe(data); setStatus("signed-in"); } else { setMe(undefined); setStatus("anonymous"); }
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- the session is read from the server on mount; the state lands after the await
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    const drop = () => { setMe(undefined); setStatus("anonymous"); };
    window.addEventListener(UNAUTHENTICATED, drop);
    return () => window.removeEventListener(UNAUTHENTICATED, drop);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error, response } = await api.POST("/auth/login", { body: { email, password } });
    if (response.ok) { await refresh(); return undefined; }
    return (error as components["schemas"]["Problem"] | undefined) ?? { reason: "internal", message: `Sign-in failed (${response.status}).`, request_id: "" };
  }, [refresh]);

  const exchange = useCallback(async (idToken: string) => {
    const { response } = await api.POST("/auth/sessions", { body: { id_token: idToken } });
    return response.ok;
  }, []);

  const signOut = useCallback(async (opts?: { returnTo?: string }) => {
    const { data, response } = await api.POST("/auth/logout");
    // The remembered scope is this person's, not the browser's (#184), and so
    // is the remembered address: a person who chose to leave leaves nothing on
    // the sign-in page for the next one (#2054).
    forgetScope();
    forgetAddress();
    setMe(undefined);
    // Zenith's session is ended whatever follows: the server ended it before
    // it answered. A session minted from an identity also holds one at the
    // provider, and the browser goes to end it (ADR-0261 decision 1a).
    const provider = response.status === 200 && data && "provider_sign_out" in data ? data.provider_sign_out : undefined;
    if (!provider) { setStatus("anonymous"); return; }
    setStatus("leaving");
    leave.assign(await signOutAddress(provider, opts?.returnTo));
  }, []);

  return <Ctx.Provider value={{ me, status, signIn, exchange, signOut, refresh }}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}

/** The session where there is one: for a hook drawn inside the shell and in a
 * test that renders a component without it alike. */
export function useSessionIfAny(): Session | undefined {
  return useContext(Ctx);
}
